/**
 * yjs Web Worker client — main-thread API for routing CPU-heavy Y.js work
 * to a single dedicated Web Worker.
 *
 * Singleton design
 * ────────────────
 * Unlike the server's worker pool (`websocket-server/src/yjs/yjsWorkerPool.js`),
 * the browser only needs ONE worker:
 *   - There's only one main thread to keep responsive
 *   - Browsers don't expose a cheap kill-and-restart primitive like Node's
 *     `worker.terminate()`; spawning workers has setup cost
 *   - Concurrent encode requests on the same client are rare in practice
 *     (one user, one tab)
 *
 * The single worker handles requests serially by ID. If we ever observe
 * head-of-line blocking (e.g. a 5 s encode delays a small ping), we can
 * promote this to a small pool — the public API doesn't change.
 *
 * Kill-switch
 * ───────────
 * `VITE_YJS_WEB_WORKER_ENABLED=false` (or `localStorage.setItem('yjs_worker_disabled', '1')`)
 * causes the client to fall back to synchronous main-thread execution.
 * Same code paths, same promises, just executed in-line. This is the
 * emergency rollback if the worker bundle ships with a regression.
 *
 * Lifecycle
 * ─────────
 * The worker is lazily spawned on first use. `dispose()` terminates it
 * and aborts pending requests; subsequent calls re-spawn. In practice
 * we never call `dispose()` — the worker lives for the tab's lifetime.
 *
 * Reference: `/workspace/.cursor/plans/websocket_robustness_refactor_b886ced2.plan.md`
 */

import * as Y from 'yjs';
import {
	OP_ENCODE_FROM_UPDATES,
	OP_MERGE_UPDATES,
	OP_ENCODE_FROM_STATE,
	OP_APPLY_UPDATE_TO_STATE,
	OP_PING,
	type WorkerRequest,
	type WorkerResponse,
	type WorkerOp,
	type EncodeFromUpdatesPayload,
	type MergeUpdatesPayload,
	type EncodeFromStatePayload,
	type ApplyUpdateToStatePayload,
} from './yjsWorkerProtocol';

// ── Config ──────────────────────────────────────────────────────────────────

const DEFAULT_TASK_TIMEOUT_MS = 15_000;

// Read the kill-switch lazily so a localStorage flip doesn't require a
// page reload.
function isWorkerEnabledByDefault(): boolean {
	try {
		// localStorage takes precedence — easier to flip without a redeploy.
		if (typeof localStorage !== 'undefined') {
			if (localStorage.getItem('yjs_worker_disabled') === '1') return false;
			if (localStorage.getItem('yjs_worker_enabled') === '1') return true;
		}
	} catch { /* SSR / privacy mode — fall through to env */ }
	// Vite exposes import.meta.env values prefixed with VITE_ to the bundle.
	const fromEnv = (import.meta as { env?: Record<string, string | undefined> })?.env?.VITE_YJS_WEB_WORKER_ENABLED;
	if (fromEnv === 'false') return false;
	if (fromEnv === 'true') return true;
	// Default: enabled. The kill-switch is opt-in.
	return true;
}

// ── Errors ──────────────────────────────────────────────────────────────────

export class YjsWorkerClientError extends Error {
	code: string;
	constructor(code: string, message: string) {
		super(message);
		this.name = 'YjsWorkerClientError';
		this.code = code;
	}
}

// ── Pending request bookkeeping ─────────────────────────────────────────────

interface PendingRequest {
	resolve: (result: unknown) => void;
	reject: (err: unknown) => void;
	timer: ReturnType<typeof setTimeout>;
	op: WorkerOp;
}

// ── Synchronous fallback (used when worker is disabled or fails to boot) ────
//
// Mirrors the worker's dispatch() exactly so behavior is identical regardless
// of which path the request takes. Never instantiate the Y.Doc more than once
// per call — this IS the chandler-pattern blocking call we're trying to avoid,
// so it should only run when the worker is unavailable.

function syncDispatch(op: WorkerOp, payload: unknown): unknown {
	function applyUpdatesToFreshDoc(updates: Uint8Array[]): Uint8Array {
		const doc = new Y.Doc({ gc: true });
		try {
			Y.transact(doc, () => {
				for (const u of updates) {
					if (!(u instanceof Uint8Array)) {
						throw new TypeError(`update must be Uint8Array, got ${typeof u}`);
					}
					// eslint-disable-next-line no-restricted-syntax -- documented sync fallback for when the Web Worker is disabled
					Y.applyUpdateV2(doc, u);
				}
			}, 'yjs-worker-sync', false);
			// eslint-disable-next-line no-restricted-syntax -- documented sync fallback for when the Web Worker is disabled
			return Y.encodeStateAsUpdateV2(doc);
		} finally {
			try { doc.destroy(); } catch { /* best-effort */ }
		}
	}
	switch (op) {
		case OP_ENCODE_FROM_UPDATES: {
			const p = payload as EncodeFromUpdatesPayload;
			return applyUpdatesToFreshDoc(p.updates);
		}
		case OP_MERGE_UPDATES: {
			const p = payload as MergeUpdatesPayload;
			// eslint-disable-next-line no-restricted-syntax -- documented sync fallback for when the Web Worker is disabled
			return Y.mergeUpdatesV2(p.updates);
		}
		case OP_ENCODE_FROM_STATE: {
			const p = payload as EncodeFromStatePayload;
			return applyUpdatesToFreshDoc([p.state]);
		}
		case OP_APPLY_UPDATE_TO_STATE: {
			const p = payload as ApplyUpdateToStatePayload;
			return applyUpdatesToFreshDoc([p.state, p.update]);
		}
		case OP_PING:
			return 'pong' as const;
		default:
			throw new YjsWorkerClientError('UNKNOWN_OP', `unknown op '${op}'`);
	}
}

// ── Client implementation ───────────────────────────────────────────────────

interface YjsWorkerClientOptions {
	enabled?: boolean;
	taskTimeoutMs?: number;
	/** Test seam — replaces `new Worker(...)` so unit tests can inject a fake. */
	workerFactory?: () => Worker;
	/** Test seam — defaults to console. */
	logger?: { log: (...a: unknown[]) => void; warn: (...a: unknown[]) => void; error: (...a: unknown[]) => void };
}

export interface YjsWorkerClient {
	encodeFromUpdates(updates: Uint8Array[]): Promise<Uint8Array>;
	mergeUpdates(updates: Uint8Array[]): Promise<Uint8Array>;
	encodeFromState(state: Uint8Array): Promise<Uint8Array>;
	applyUpdateToState(state: Uint8Array, update: Uint8Array): Promise<Uint8Array>;
	ping(): Promise<'pong'>;
	dispose(): void;
	stats(): {
		enabled: boolean;
		workerSpawned: boolean;
		pending: number;
		totalRequests: number;
		errors: number;
		fallbackUsed: number;
	};
}

export function createYjsWorkerClient(options: YjsWorkerClientOptions = {}): YjsWorkerClient {
	const {
		enabled = isWorkerEnabledByDefault(),
		taskTimeoutMs = DEFAULT_TASK_TIMEOUT_MS,
		workerFactory,
		logger = console,
	} = options;

	let worker: Worker | null = null;
	let nextId = 1;
	const pending = new Map<number, PendingRequest>();
	const counters = { totalRequests: 0, errors: 0, fallbackUsed: 0 };
	let disposed = false;

	function spawnWorker(): Worker {
		if (workerFactory) return workerFactory();
		// Vite recognizes `new Worker(new URL(..., import.meta.url), { type: 'module' })`
		// and bundles `yjsWorker.worker.ts` as a separate chunk.
		return new Worker(new URL('./yjsWorker.worker.ts', import.meta.url), { type: 'module' });
	}

	function ensureWorker(): Worker {
		if (worker) return worker;
		worker = spawnWorker();
		worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => {
			const msg = event.data;
			if (!msg || typeof msg.id !== 'number') return;
			const entry = pending.get(msg.id);
			if (!entry) return;
			pending.delete(msg.id);
			clearTimeout(entry.timer);
			if (msg.ok) {
				entry.resolve(msg.result);
			} else {
				const err = new YjsWorkerClientError('WORKER_THREW', msg.error);
				if (msg.stack) err.stack = msg.stack;
				counters.errors++;
				entry.reject(err);
			}
		});
		worker.addEventListener('error', (event: ErrorEvent) => {
			logger.error('[yjs-worker-client] worker error:', event.message, event.error);
			failAllPending(new YjsWorkerClientError('WORKER_ERROR', event.message || 'worker errored'));
			disposeWorker();
		});
		worker.addEventListener('messageerror', (event) => {
			logger.error('[yjs-worker-client] worker messageerror:', event);
		});
		return worker;
	}

	function failAllPending(err: YjsWorkerClientError) {
		for (const [, entry] of pending) {
			clearTimeout(entry.timer);
			entry.reject(err);
		}
		pending.clear();
	}

	function disposeWorker() {
		if (worker) {
			try { worker.terminate(); } catch { /* best-effort */ }
			worker = null;
		}
	}

	async function exec(op: WorkerOp, payload: unknown, transferList: Transferable[] = []): Promise<unknown> {
		if (disposed) throw new YjsWorkerClientError('DISPOSED', 'client has been disposed');
		counters.totalRequests++;

		// Synchronous fallback path. We still return a Promise so the public
		// API is identical — callers always `await`.
		if (!enabled) {
			counters.fallbackUsed++;
			try {
				return syncDispatch(op, payload);
			} catch (err) {
				counters.errors++;
				throw err;
			}
		}

		const w = ensureWorker();
		return new Promise<unknown>((resolve, reject) => {
			const id = nextId++;
			const timer = setTimeout(() => {
				pending.delete(id);
				logger.error(`[yjs-worker-client] op=${op} id=${id} timed out after ${taskTimeoutMs}ms — terminating worker`);
				// A timeout means the worker is wedged. Killing it is the
				// only way to unblock the queue. ensureWorker() will spawn a
				// fresh one on the next call.
				disposeWorker();
				counters.errors++;
				reject(new YjsWorkerClientError('TASK_TIMEOUT', `task timed out after ${taskTimeoutMs}ms (op=${op})`));
			}, taskTimeoutMs);
			pending.set(id, { resolve, reject, timer, op });
			const req: WorkerRequest = { id, op, payload };
			try {
				w.postMessage(req, transferList);
			} catch (err) {
				pending.delete(id);
				clearTimeout(timer);
				counters.errors++;
				reject(new YjsWorkerClientError('POST_FAILED', `postMessage failed: ${(err as Error).message}`));
			}
		});
	}

	// ── Public per-op API ──────────────────────────────────────────────────

	async function encodeFromUpdates(updates: Uint8Array[]): Promise<Uint8Array> {
		const transferList = enabled ? updates.map(u => u.buffer) : [];
		return exec(OP_ENCODE_FROM_UPDATES, { updates }, transferList) as Promise<Uint8Array>;
	}

	async function mergeUpdates(updates: Uint8Array[]): Promise<Uint8Array> {
		const transferList = enabled ? updates.map(u => u.buffer) : [];
		return exec(OP_MERGE_UPDATES, { updates }, transferList) as Promise<Uint8Array>;
	}

	async function encodeFromState(state: Uint8Array): Promise<Uint8Array> {
		const transferList = enabled ? [state.buffer] : [];
		return exec(OP_ENCODE_FROM_STATE, { state }, transferList) as Promise<Uint8Array>;
	}

	async function applyUpdateToState(state: Uint8Array, update: Uint8Array): Promise<Uint8Array> {
		const transferList = enabled ? [state.buffer, update.buffer] : [];
		return exec(OP_APPLY_UPDATE_TO_STATE, { state, update }, transferList) as Promise<Uint8Array>;
	}

	async function ping(): Promise<'pong'> {
		return exec(OP_PING, {}) as Promise<'pong'>;
	}

	function dispose() {
		disposed = true;
		failAllPending(new YjsWorkerClientError('DISPOSED', 'client has been disposed'));
		disposeWorker();
	}

	function stats() {
		return {
			enabled,
			workerSpawned: worker !== null,
			pending: pending.size,
			totalRequests: counters.totalRequests,
			errors: counters.errors,
			fallbackUsed: counters.fallbackUsed,
		};
	}

	return {
		encodeFromUpdates,
		mergeUpdates,
		encodeFromState,
		applyUpdateToState,
		ping,
		dispose,
		stats,
	};
}

// ── Module-level singleton ──────────────────────────────────────────────────
// Most callers want a single shared worker for the tab's lifetime — there's
// no benefit to spawning multiple workers on the client. Tests can construct
// their own client via `createYjsWorkerClient()` with injected workerFactory.

let _singleton: YjsWorkerClient | null = null;

export function getYjsWorkerClient(): YjsWorkerClient {
	if (!_singleton) _singleton = createYjsWorkerClient();
	return _singleton;
}

/**
 * Reset the module-level singleton. Tests use this between cases; production
 * code should not call this. After reset, the next `getYjsWorkerClient()` call
 * spawns a fresh client (and worker).
 */
export function _resetYjsWorkerClientSingletonForTests(): void {
	if (_singleton) _singleton.dispose();
	_singleton = null;
}
